import { FC, useState, FormEvent } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useApolloClient } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { getLocalizationCountryCookie } from '@app/helpers/localization';
import TextField from '@app/components/input/TextField';

import messages from '../Address.messages';
import { AddressFormFields } from '../Address.constants';
import { VAT_NUMBER_VALIDITY_QUERY } from './AddressForm.queries';
import {
  VatNumberValidityQuery,
  VatNumberValidityQueryVariables,
} from './__generated__/VatNumberValidityQuery';
import ValidationMessage from '../ValidationMessage/ValidationMessage';

export const VatNumberField: FC<{ namespace: string }> = ({ namespace }) => {
  const apolloClient = useApolloClient();
  const { errors } = useFormContext();
  const [isValidating, setIsValidating] = useState(false);
  const selectedCountryCode = getLocalizationCountryCookie();

  const validate = async (vatNumber: string) => {
    if (!vatNumber) return true;

    setIsValidating(true);

    const response = await apolloClient.query<
      VatNumberValidityQuery,
      VatNumberValidityQueryVariables
    >({
      query: VAT_NUMBER_VALIDITY_QUERY,
      variables: { countryCode: selectedCountryCode, vatNumber },
    });

    const data = response.data.magento2?.checkVatNumber;

    setIsValidating(false);

    return (
      data?.isValid || (
        <ValidationMessage type="invalid" fieldNameKey="vatNumber" />
      )
    );
  };

  return (
    <Controller
      defaultValue=""
      name={`${namespace}.${AddressFormFields.VatNumber}`}
      render={({ ref, onChange, ...props }) => (
        <TextField
          autoComplete="nope"
          loading={isValidating}
          maxLength={128}
          isValid={!errors[namespace]?.[AddressFormFields.VatNumber]}
          formLabel={
            <>
              <FormattedMessage {...messages.vatNumber} /> (
              <FormattedMessage {...messages.optional} />)
            </>
          }
          innerRef={ref}
          onChange={(e: FormEvent<HTMLInputElement>) => {
            const value = (e?.target as HTMLInputElement).value;
            onChange(value);
          }}
          {...props}
        />
      )}
      // @ts-expect-error: TS only support string, not ReactElement (react-hook-form.com/api#register)
      rules={{ validate }}
    />
  );
};
